"use client";

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  ArrowUp,
  ChartBarBig,
  Check,
  CirclePause,
  GitCompareArrows,
  LoaderCircle,
  Pause,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDictionary } from "@/lib/useDictionary";
import { useLocalePath } from "@/lib/useLocale";
import type { McpShowcaseChart, McpShowcaseStep } from "@/dictionaries/types";

// 情境卡片 icon，依字典 mcpShowcase.scenarios 的順序對應
const SCENARIO_ICONS = [ChartBarBig, GitCompareArrows, CirclePause];

const AI_CLIENTS = [
  { name: "Claude", icon: "/icon/claude.png" },
  { name: "ChatGPT", icon: "/icon/gpt.png" },
  { name: "Gemini", icon: "/icon/gemini.png" },
];

// compare 圖的兩個系列色（藍 / 琥珀），已過色盲辨識檢查；琥珀對白底對比不足，所以每根 bar 都有數值標籤
const SERIES_COLORS = ["#2563eb", "#e08a0b"];
// ranking 圖：第一名用主色，其餘用淡一階的藍
const RANKING_MUTED = "#a9c2f7";

// 時間軸（毫秒）
const LEAD_MS = 500; // 進入畫面後先停一下再開始打字
const HOLD_MS = 3500; // 劇本播完停留，讓人看完再切下一個情境
const TOOL_RUN_MS = 1300; // 工具呼叫轉圈的時間
const CHART_GROW_MS = 1100; // 圖表長出來的時間，需大於 (列數 - 1) * ROW_STAGGER_MS + ROW_GROW_MS
const ROW_STAGGER_MS = 110;
const ROW_GROW_MS = 700;
const PAUSE_STAGGER_MS = 180; // 暫停後表格狀態逐列切換

type TimedStep = {
  step: McpShowcaseStep;
  start: number; // 出現的時間；user 是開始在輸入框打字的時間
  done: number; // user 送出、tool 完成、text 打完、chart 長完
  charTimes: number[]; // user / text 每個字出現的累積時間
};

type Timeline = {
  steps: TimedStep[];
  total: number;
  pausedAt: number; // action: "pause" 的工具完成時間，沒有就是 Infinity
};

const isWideChar = (ch: string) => /[⺀-鿿＀-￯]/.test(ch);

// 中文字比英數慢一點，兩種語系的打字節奏才會接近
function buildCharTimes(text: string, wideMs: number, narrowMs: number) {
  const times: number[] = [];
  let acc = 0;
  for (const ch of Array.from(text)) {
    acc += isWideChar(ch) ? wideMs : narrowMs;
    times.push(acc);
  }
  return times;
}

function countRevealed(times: number[], elapsed: number) {
  let count = 0;
  while (count < times.length && times[count] <= elapsed) count++;
  return count;
}

const stripBold = (text: string) => text.replace(/\*\*/g, "");

function parseBold(text: string) {
  return text
    .split(/\*\*(.+?)\*\*/)
    .map((part, index) => ({ text: part, bold: index % 2 === 1 }))
    .filter((segment) => segment.text);
}

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);
const easeOut = (value: number) => 1 - Math.pow(1 - value, 3);

function buildTimeline(steps: McpShowcaseStep[]): Timeline {
  let cursor = LEAD_MS;
  let pausedAt = Infinity;

  const timed = steps.map((step) => {
    const start = cursor;
    let done = start;
    let charTimes: number[] = [];

    switch (step.kind) {
      case "user":
        charTimes = buildCharTimes(step.text, 70, 30);
        done = start + (charTimes.at(-1) ?? 0) + 250;
        cursor = done + 450;
        break;
      case "tool":
        done = start + TOOL_RUN_MS;
        cursor = done + 250;
        if (step.action === "pause") pausedAt = done;
        break;
      case "text":
        charTimes = buildCharTimes(stripBold(step.text), 28, 11);
        done = start + (charTimes.at(-1) ?? 0);
        cursor = done + 350;
        break;
      case "chart":
        done = start + CHART_GROW_MS;
        cursor = done + 600;
        break;
    }

    return { step, start, done, charTimes };
  });

  return { steps: timed, total: cursor + HOLD_MS, pausedAt };
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

// framer 的 useReducedMotion 在 hydration 當下就讀 matchMedia，會跟 SSR 的 HTML 對不起來；
// useSyncExternalStore 會先用 server 值 (false) hydrate，再切成實際偏好
function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );
}

interface McpChatShowcaseProps {
  className?: string;
}

export default function McpChatShowcase({ className }: McpChatShowcaseProps) {
  const dict = useDictionary();
  const copy = dict.mcpShowcase;
  const reduceMotion = usePrefersReducedMotion();

  const timelines = useMemo(
    () => copy.scenarios.map((scenario) => buildTimeline(scenario.steps)),
    [copy],
  );

  const [playback, setPlayback] = useState({ index: 0, elapsed: 0 });

  const windowRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // 不設 once：捲出畫面就暫停，回來接著播
  const isInView = useInView(windowRef, { amount: 0.4 });
  const playing = isInView && !reduceMotion;

  // 單一時鐘推進整段劇本；分頁在背景時 rAF 本來就會停，dt 上限避免回來時跳太多
  useEffect(() => {
    if (!playing) return;

    let frame = 0;
    let last = performance.now();
    const tick = () => {
      // rAF 給的時間戳是影格開始時間，可能比剛才的 performance.now() 還早，差值要夾在 0 以上
      const now = performance.now();
      const dt = Math.min(Math.max(now - last, 0), 100);
      last = now;
      setPlayback((prev) => {
        const elapsed = prev.elapsed + dt;
        if (elapsed < timelines[prev.index].total) {
          return { index: prev.index, elapsed };
        }
        return { index: (prev.index + 1) % timelines.length, elapsed: 0 };
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [playing, timelines]);

  // 內容長高就捲到底，只捲對話框本身，不用 scrollIntoView 以免拉動整頁
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;

    const observer = new ResizeObserver(() => {
      scroller.scrollTo({
        top: scroller.scrollHeight,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [reduceMotion]);

  const active = Math.min(playback.index, timelines.length - 1);
  const timeline = timelines[active];
  // 偏好減少動態時直接顯示整段對話的最終狀態
  const t = reduceMotion ? timeline.total : playback.elapsed;

  const visibleSteps = timeline.steps.filter(
    (timed) => t >= (timed.step.kind === "user" ? timed.done : timed.start),
  );
  const typingUser = timeline.steps.find(
    (timed) =>
      timed.step.kind === "user" && t >= timed.start && t < timed.done,
  );

  const appear = reduceMotion
    ? { initial: false as const }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
      };

  const renderStep = (timed: TimedStep): ReactNode => {
    const { step } = timed;
    switch (step.kind) {
      case "user":
        return (
          <div className="flex justify-end">
            <p className="max-w-[85%] rounded-2xl rounded-br-md bg-[var(--blue-700)] px-3.5 py-2 text-[13px] leading-relaxed text-white sm:text-sm">
              {step.text}
            </p>
          </div>
        );
      case "tool":
        return (
          <ToolCall
            label={step.label}
            detail={step.detail}
            done={t >= timed.done}
          />
        );
      case "text":
        return (
          <TypedText
            text={step.text}
            count={countRevealed(timed.charTimes, t - timed.start)}
            typing={t < timed.done}
          />
        );
      case "chart":
        return (
          <ChartCard
            chart={step.chart}
            local={t - timed.start}
            t={t}
            pausedAt={timeline.pausedAt}
            reduceMotion={!!reduceMotion}
          />
        );
    }
  };

  return (
    <section
      className={cn(
        "relative overflow-hidden bg-[linear-gradient(180deg,var(--darker-blue)_0%,var(--light-blue)_100%)] py-20",
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mb-12 max-w-3xl text-center"
        >

          <h2 className="mt-4 flex flex-wrap items-baseline justify-center gap-x-3 gap-y-2 text-[var(--blue-900)]">
            {copy.headingBefore ? (
              <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
                {copy.headingBefore}
              </span>
            ) : null}
            <span className="hero-ink text-6xl font-black sm:text-7xl lg:text-7xl">
              {copy.headingAccent}
            </span>
            <span className="text-3xl font-bold sm:text-4xl lg:text-5xl">
              {copy.headingAfter}
            </span>
          </h2>
        </motion.div>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-8">
          {/* 情境卡片：手機三欄只放 icon + 標題，桌機直排並與對話框同高 */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:h-[540px] lg:grid-cols-1 lg:grid-rows-3">
            {copy.scenarios.map((scenario, index) => {
              const Icon = SCENARIO_ICONS[index % SCENARIO_ICONS.length];
              const isActive = index === active;
              const progress = isActive ? clamp01(t / timeline.total) : 0;

              return (
                <button
                  key={scenario.title}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setPlayback({ index, elapsed: 0 })}
                  className={cn(
                    "relative flex flex-col items-start gap-2 overflow-hidden rounded-2xl border p-3 pb-4 text-left shadow-[0_20px_50px_rgba(148,163,184,0.12)] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--blue-500)] sm:gap-3 sm:p-5 sm:pb-6 lg:justify-center",
                    isActive
                      ? "border-[var(--blue-500)] bg-white"
                      : "border-white/70 bg-white/70 hover:border-[var(--blue-300)] hover:bg-white/90",
                  )}
                >
                  <span className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-colors sm:h-10 sm:w-10",
                        isActive
                          ? "bg-[var(--blue-700)] text-white"
                          : "bg-[var(--light-blue)] text-[var(--blue-700)]",
                      )}
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                    </span>
                    <span className="text-[13px] font-bold leading-snug text-[var(--blue-900)] sm:text-lg">
                      {scenario.title}
                    </span>
                  </span>
                  <span className="hidden text-sm leading-relaxed text-[var(--blue-900)]/65 sm:block">
                    {scenario.description}
                  </span>
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-1 bg-[var(--blue-900)]/[0.06]"
                  >
                    <span
                      className="block h-full origin-left bg-[var(--blue-700)]"
                      style={{ transform: `scaleX(${progress})` }}
                    />
                  </span>
                </button>
              );
            })}
          </div>

          <div>
            {/* 示意對話框：純動畫，內容另以 sr-only 提供給讀屏與搜尋引擎 */}
            <div
              ref={windowRef}
              aria-hidden="true"
              className="flex h-[500px] flex-col overflow-hidden rounded-2xl border border-white/70 bg-white shadow-[0_30px_70px_rgba(56,132,255,0.16)] sm:h-[540px]"
            >
              <div className="flex h-10 shrink-0 items-center gap-3 border-b border-slate-200/80 bg-slate-50/80 px-3.5">
                <span className="flex shrink-0 gap-1.5">
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className="h-2.5 w-2.5 rounded-full bg-slate-300/80"
                    />
                  ))}
                </span>
                <span className="min-w-0 flex-1 truncate text-center text-xs font-semibold tracking-wide text-slate-600">
                  {copy.windowTitle}
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {AI_CLIENTS.map((client) => (
                    <Image
                      key={client.name}
                      src={client.icon}
                      alt={client.name}
                      width={16}
                      height={16}
                      className="h-4 w-4 object-contain"
                    />
                  ))}
                </span>
              </div>

              <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-y-auto bg-[#fbfdff] px-3 py-4 [scrollbar-width:none] sm:px-5 [&::-webkit-scrollbar]:hidden"
              >
                {/* ResizeObserver 要掛在不會重新掛載的外層；切換情境時換掉的是內層 */}
                <div ref={contentRef}>
                  <div key={active} className="space-y-3">
                    {visibleSteps.map((timed, index) => (
                      <motion.div key={index} {...appear}>
                        {renderStep(timed)}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200/80 bg-white p-3">
                <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/60 py-2 pl-3.5 pr-2">
                  <p
                    className={cn(
                      "min-h-7 min-w-0 flex-1 py-1 text-[13px] leading-5",
                      typingUser ? "text-slate-800" : "text-slate-400",
                    )}
                  >
                    {typingUser && typingUser.step.kind === "user" ? (
                      <>
                        {Array.from(typingUser.step.text)
                          .slice(
                            0,
                            countRevealed(
                              typingUser.charTimes,
                              t - typingUser.start,
                            ),
                          )
                          .join("")}
                        <Caret />
                      </>
                    ) : (
                      copy.inputPlaceholder
                    )}
                  </p>
                  <span
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                      typingUser
                        ? "bg-[var(--blue-700)] text-white"
                        : "bg-slate-200 text-slate-400",
                    )}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-[var(--blue-900)]/45">
              {copy.disclaimer}
            </p>
          </div>
        </div>


        <div className="sr-only">
          {copy.scenarios.map((scenario) => (
            <div key={scenario.title}>
              <h3>{scenario.title}</h3>
              <ul>
                {scenario.steps.map((step, index) =>
                  step.kind === "user" || step.kind === "text" ? (
                    <li key={index}>{stripBold(step.text)}</li>
                  ) : null,
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Caret() {
  return (
    <span className="ml-px inline-block h-[1em] w-px translate-y-[2px] animate-pulse bg-current" />
  );
}

function ToolCall({
  label,
  detail,
  done,
}: {
  label: string;
  detail: string;
  done: boolean;
}) {
  return (
    <div className="inline-flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {done ? (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-2.5 w-2.5" strokeWidth={3} />
        </span>
      ) : (
        <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-[var(--blue-500)]" />
      )}
      <span
        className={cn(
          "shrink-0 font-medium text-slate-700",
          !done && "animate-pulse",
        )}
      >
        {label}
      </span>
      <span className="min-w-0 truncate text-slate-400">{detail}</span>
    </div>
  );
}

function TypedText({
  text,
  count,
  typing,
}: {
  text: string;
  count: number;
  typing: boolean;
}) {
  const parts: ReactNode[] = [];
  let remaining = count;
  for (const [index, segment] of parseBold(text).entries()) {
    if (remaining <= 0) break;
    const chars = Array.from(segment.text);
    const shown = chars.slice(0, remaining).join("");
    remaining -= chars.length;
    parts.push(
      segment.bold ? (
        <strong key={index} className="font-semibold text-[var(--blue-900)]">
          {shown}
        </strong>
      ) : (
        <span key={index}>{shown}</span>
      ),
    );
  }

  return (
    <p className="text-[13px] leading-relaxed text-slate-700 sm:text-sm">
      {parts}
      {typing ? <Caret /> : null}
    </p>
  );
}

function ChartCard({
  chart,
  local,
  t,
  pausedAt,
  reduceMotion,
}: {
  chart: McpShowcaseChart;
  local: number; // 圖表出現後經過的毫秒
  t: number;
  pausedAt: number;
  reduceMotion: boolean;
}) {
  const rowProgress = (index: number) =>
    easeOut(clamp01((local - index * ROW_STAGGER_MS) / ROW_GROW_MS));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-[12px] font-semibold text-slate-700">
          {chart.title}
        </p>
        {chart.kind === "compare" ? (
          <span className="flex items-center gap-3">
            {chart.series.map((name, index) => (
              <span
                key={name}
                className="flex items-center gap-1.5 text-[11px] text-slate-500"
              >
                <span
                  className="h-2 w-2 rounded-[2px]"
                  style={{ backgroundColor: SERIES_COLORS[index] }}
                />
                {name}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      {chart.kind === "ranking" ? (
        <RankingChart rows={chart.rows} rowProgress={rowProgress} />
      ) : null}

      {chart.kind === "compare" ? (
        <div className="space-y-3">
          {chart.rows.map((row, rowIndex) => {
            const max = Math.max(...row.values);
            const progress = rowProgress(rowIndex);
            return (
              <div key={row.label}>
                <p className="mb-1 text-[11px] text-slate-500">{row.label}</p>
                <div className="space-y-[3px] border-l border-slate-200">
                  {row.values.map((value, seriesIndex) => (
                    <div
                      key={seriesIndex}
                      className="flex items-center gap-2"
                    >
                      <div
                        className="h-2 rounded-r-[4px]"
                        style={{
                          width: `calc((100% - 5rem) * ${(value / max) * progress})`,
                          backgroundColor: SERIES_COLORS[seriesIndex],
                        }}
                      />
                      <span
                        className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-slate-800"
                        style={{ opacity: progress }}
                      >
                        {row.display[seriesIndex]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {chart.kind === "status" ? (
        <div className="text-[11px]">
          <div className="grid grid-cols-[minmax(0,1fr)_2.25rem_3.75rem_3.5rem] gap-x-2 border-b border-slate-100 pb-1.5 text-[10px] font-medium text-slate-400">
            {chart.columns.map((column, index) => (
              <span
                key={column}
                className={cn("truncate", index > 0 && "text-right")}
              >
                {column}
              </span>
            ))}
          </div>
          {chart.rows.map((row, index) => {
            const paused = t >= pausedAt + index * PAUSE_STAGGER_MS;
            return (
              <div
                key={row.label}
                className="grid grid-cols-[minmax(0,1fr)_2.25rem_3.75rem_3.5rem] items-center gap-x-2 border-b border-slate-100 py-2 last:border-0"
                style={{ opacity: rowProgress(index) }}
              >
                <span
                  className={cn(
                    "truncate transition-colors",
                    paused ? "text-slate-400" : "text-slate-700",
                  )}
                >
                  {row.label}
                </span>
                <span className="text-right font-semibold tabular-nums text-slate-800">
                  {row.roas}
                </span>
                <span className="text-right tabular-nums text-slate-600">
                  {row.spend}
                </span>
                <span className="flex justify-end">
                  <motion.span
                    key={paused ? "paused" : "active"}
                    initial={reduceMotion ? false : { scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.25 }}
                    className={cn(
                      "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                      paused
                        ? "bg-slate-100 text-slate-500"
                        : "bg-emerald-50 text-emerald-700",
                    )}
                  >
                    {paused ? (
                      <Pause className="h-2.5 w-2.5" />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    )}
                    {paused ? chart.pausedLabel : chart.activeLabel}
                  </motion.span>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function RankingChart({
  rows,
  rowProgress,
}: {
  rows: { label: string; value: number; display: string }[];
  rowProgress: (index: number) => number;
}) {
  const max = Math.max(...rows.map((row) => row.value));

  return (
    <div className="space-y-2.5">
      {rows.map((row, index) => {
        const progress = rowProgress(index);
        return (
          <div key={row.label}>
            <p className="mb-1 truncate text-[11px] text-slate-600">
              {row.label}
            </p>
            <div className="flex items-center gap-2 border-l border-slate-200">
              <div
                className="h-2.5 rounded-r-[4px]"
                style={{
                  width: `calc((100% - 2.5rem) * ${(row.value / max) * progress})`,
                  backgroundColor: index === 0 ? SERIES_COLORS[0] : RANKING_MUTED,
                }}
              />
              <span
                className="text-[11px] font-semibold tabular-nums text-slate-800"
                style={{ opacity: progress }}
              >
                {row.display}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
